import React, { useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import type { RJSFSchema, RegistryFieldsType, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import cloneDeep from 'lodash/cloneDeep';

import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { categories } from '@luxuryescapes/lib-global';

import AmenityForm from '~/components/Common/Forms/AmenityForm';
import Amenities from '~/components/Common/Forms/fields/Amenities';
import AIGeneratorTextAreaWidget from '~/components/Common/Forms/widgets/AIGeneratorTextAreaWidget';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import { normaliseRoomDraftFormData } from './utils';
import type { FlashOfferRoomDraft } from './utils';

const fields: RegistryFieldsType = { amenities: Amenities as unknown as RegistryFieldsType[string] };

interface Props {
  amenityOptions: Array<string>;
  channelManager?: string | null;
  disabled?: boolean;
  onAddAmenityOption: (amenityName: string) => void;
  onChange: (updates: Partial<FlashOfferRoomDraft>) => void;
  propertyCategory?: string | null;
  propertyName?: string | null;
  roomDraft: FlashOfferRoomDraft;
  schema?: RJSFSchema;
}

function getRoomFormData(roomDraft: FlashOfferRoomDraft): Record<string, unknown> {
  const {
    draftId: _draftId,
    existingRoomName: _existingRoomName,
    shouldCreate: _shouldCreate,
    ...formData
  } = roomDraft;

  return formData;
}

function prepareSchema(schema: RJSFSchema | undefined, propertyCategory?: string | null) {
  const nextSchema = cloneDeep(schema);

  if (!nextSchema?.properties) {
    return undefined;
  }

  const bedGroups = nextSchema.properties.bed_groups as RJSFSchema | undefined;
  const bedGroupItems = bedGroups?.items as RJSFSchema | undefined;
  const bedGroupProperties = bedGroupItems?.properties;

  if (bedGroupItems && bedGroupProperties) {
    bedGroupItems.properties = {
      id: bedGroupProperties.id,
      description: {
        type: 'string',
      },
      configuration: bedGroupProperties.configuration,
    };
  }

  delete nextSchema.properties.amenities_ordering;

  delete nextSchema.properties.room_policies;

  const numberOfBedrooms = nextSchema.properties.number_of_bedrooms as RJSFSchema | undefined;
  const isUniqueStay = categories.isUniqueStay(propertyCategory);

  if (numberOfBedrooms) {
    numberOfBedrooms.minimum = isUniqueStay ? 1 : 0;
  }

  const required = nextSchema.required ?? [];
  nextSchema.required = isUniqueStay
    ? Array.from(new Set(required.concat('number_of_bedrooms')))
    : required.filter((key) => key !== 'number_of_bedrooms');

  return nextSchema;
}

function buildUiSchema(channelManager?: string | null): UiSchema {
  return {
    'ui:submitButtonOptions': { norender: true },
    property_id: { 'ui:widget': 'hidden' },
    amenities: { 'ui:field': 'amenities' },
    description: {
      'ui:widget': AIGeneratorTextAreaWidget,
    },
    room_type_code: {
      'ui:widget': channelManager !== PROPERTY_CHANNEL_MANAGERS.selfManaged ? 'text' : 'hidden',
    },
    number_of_bedrooms: { 'ui:widget': 'text' },
    bed_groups: {
      'ui:options': {
        removable: true,
        orderable: true,
        addable: true,
      },
      items: {
        id: { 'ui:widget': 'hidden' },
        description: { 'ui:disabled': true },
        configuration: {
          'ui:options': {
            removable: true,
            orderable: true,
            addable: true,
          },
        },
      },
    },
  };
}

export default function RoomDraftForm({
  amenityOptions,
  channelManager,
  disabled,
  onAddAmenityOption,
  onChange,
  propertyCategory,
  propertyName,
  roomDraft,
  schema,
}: Props) {
  const [isAmenityDialogOpen, setIsAmenityDialogOpen] = useState(false);
  const preparedSchema = useMemo(() => prepareSchema(schema, propertyCategory), [propertyCategory, schema]);
  const uiSchema = useMemo(() => buildUiSchema(channelManager), [channelManager]);

  if (!preparedSchema) {
    return null;
  }

  return (
    <>
      <Form
        schema={preparedSchema}
        uiSchema={{
          ...uiSchema,
          description: {
            ...uiSchema.description,
            'ui:generatorContext': {
              propertyName,
              roomName: roomDraft.name,
              amenities: roomDraft.amenities,
              model: 'room-type',
              field: 'description',
            },
          },
        }}
        fields={fields}
        formData={getRoomFormData(roomDraft)}
        disabled={disabled}
        onChange={(edit) => onChange(normaliseRoomDraftFormData(edit.formData as Partial<FlashOfferRoomDraft>))}
        formContext={{
          amenityOptions,
          onStartAddingAmenities: () => setIsAmenityDialogOpen(true),
          isFlashOffer: true,
        }}
        validator={validator}
        transformErrors={(errors) =>
          errors.filter(
            (error) =>
              !(
                error.name === 'additionalProperties' &&
                error.property?.match(/^\.bed_groups\.\d+\.configuration\.\d+$/)
              ),
          )
        }
      />

      <Dialog open={isAmenityDialogOpen} onClose={() => setIsAmenityDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle id="form-dialog-title">Add a new amenity type</DialogTitle>
        <DialogContent>
          <AmenityForm
            onSubmit={onAddAmenityOption}
            onCancel={() => setIsAmenityDialogOpen(false)}
            onAfterSubmit={() => setIsAmenityDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
